import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    buckets: Array,
    color: { type: String, default: "#377dff" },
    series: Array
  }

  connect() {
    this.draw()
  }

  bucketsValueChanged() {
    this.draw()
  }

  colorValueChanged() {
    this.draw()
  }

  seriesValueChanged() {
    this.draw()
  }

  draw() {
    const canvas = this.element
    const context = canvas.getContext("2d")
    const series = this.seriesForChart()
    const buckets = series[0]?.buckets || []

    context.clearRect(0, 0, canvas.width, canvas.height)
    context.fillStyle = "#ffffff"
    context.fillRect(0, 0, canvas.width, canvas.height)

    if (series.length === 0 || buckets.length === 0) {
      this.drawEmpty(context, canvas)
      return
    }

    const padding = { top: 18, right: 18, bottom: 56, left: 38 }
    const chartWidth = canvas.width - padding.left - padding.right
    const chartHeight = canvas.height - padding.top - padding.bottom
    const leftMax = this.axisMax(series, "left")
    const rightMax = this.axisMax(series, "right")
    const gap = 8
    const barWidth = Math.max((chartWidth - gap * (buckets.length - 1)) / buckets.length, 1)
    const columnSeries = series.filter((entry) => this.chartType(entry) === "column")
    const columnGap = columnSeries.length > 1 ? 2 : 0
    const columnBarWidth = Math.max((barWidth - columnGap * (columnSeries.length - 1)) / Math.max(columnSeries.length, 1), 1)

    context.strokeStyle = "#d5dbe3"
    context.lineWidth = 1
    context.beginPath()
    context.moveTo(padding.left, padding.top)
    context.lineTo(padding.left, padding.top + chartHeight)
    context.lineTo(padding.left + chartWidth, padding.top + chartHeight)
    if (series.some((entry) => this.axisName(entry) === "right")) {
      context.lineTo(padding.left + chartWidth, padding.top)
    }
    context.stroke()

    context.font = "13px sans-serif"
    context.textAlign = "center"
    context.textBaseline = "middle"

    buckets.forEach((bucket, index) => {
      const x = padding.left + index * (barWidth + gap)
      let columnIndex = 0

      series.forEach((entry) => {
        if (this.chartType(entry) !== "column") return

        const seriesBucket = entry.buckets[index] || { count: 0 }
        const maxCount = this.axisName(entry) === "right" ? rightMax : leftMax
        const barHeight = Math.round((seriesBucket.count / maxCount) * chartHeight)
        const y = padding.top + chartHeight - barHeight
        const seriesX = x + columnIndex * (columnBarWidth + columnGap)

        context.fillStyle = entry.color || this.colorValue
        context.fillRect(seriesX, y, columnBarWidth, barHeight)

        if (seriesBucket.count > 0) {
          context.fillStyle = "#20262e"
          context.fillText(seriesBucket.count.toString(), seriesX + columnBarWidth / 2, y - 9)
        }

        columnIndex += 1
      })

      context.save()
      context.translate(x + barWidth / 2, padding.top + chartHeight + 10)
      context.rotate(-Math.PI / 7)
      context.textAlign = "right"
      context.fillStyle = "#5b6470"
      context.fillText(bucket.label, 0, 0)
      context.restore()
    })

    series.filter((entry) => this.chartType(entry) === "line").forEach((entry) => {
      this.drawLineSeries(context, entry, buckets, padding, chartWidth, chartHeight, barWidth, gap, rightMax)
    })

    if (series.length > 1) this.drawLegend(context, canvas, series)
  }

  seriesForChart() {
    if (this.hasSeriesValue && this.seriesValue.length > 0) return this.seriesValue

    return [
      {
        label: "",
        color: this.colorValue,
        buckets: this.bucketsValue || []
      }
    ]
  }

  drawLegend(context, canvas, series) {
    const legendY = canvas.height - 13
    let x = 38

    context.font = "12px sans-serif"
    context.textAlign = "left"
    context.textBaseline = "middle"

    series.forEach((entry) => {
      context.fillStyle = entry.color || this.colorValue
      context.fillRect(x, legendY - 5, 10, 10)
      context.fillStyle = "#5b6470"
      context.fillText(entry.label, x + 15, legendY)
      x += context.measureText(entry.label).width + 34
    })
  }

  drawLineSeries(context, entry, buckets, padding, chartWidth, chartHeight, barWidth, gap, maxCount) {
    const points = buckets.map((_bucket, index) => {
      const seriesBucket = entry.buckets[index] || { count: 0 }
      const x = padding.left + index * (barWidth + gap) + barWidth / 2
      const y = padding.top + chartHeight - Math.round((seriesBucket.count / maxCount) * chartHeight)
      return { x, y, count: seriesBucket.count }
    })

    context.strokeStyle = entry.color || this.colorValue
    context.lineWidth = 2
    context.beginPath()
    points.forEach((point, index) => {
      if (index === 0) {
        context.moveTo(point.x, point.y)
      } else {
        context.lineTo(point.x, point.y)
      }
    })
    context.stroke()

    points.forEach((point) => {
      context.fillStyle = entry.color || this.colorValue
      context.beginPath()
      context.arc(point.x, point.y, 3.5, 0, Math.PI * 2)
      context.fill()
    })
  }

  axisMax(series, axisName) {
    const counts = series
      .filter((entry) => this.axisName(entry) === axisName)
      .flatMap((entry) => entry.buckets.map((bucket) => bucket.count))

    return Math.max(...counts, 1)
  }

  axisName(entry) {
    return entry.y_axis || "left"
  }

  chartType(entry) {
    return entry.chart_type || "column"
  }

  drawEmpty(context, canvas) {
    context.fillStyle = "#6c757d"
    context.font = "14px sans-serif"
    context.textAlign = "center"
    context.textBaseline = "middle"
    context.fillText("No distribution data", canvas.width / 2, canvas.height / 2)
  }
}
